import { apiSlice } from '../../store/api/apiSlice';
import { ENV_CONFIG } from '../../shared/config/env.config';

// A class/section pair returned by SchlWiseClassList.
export interface TcClassSection {
  Cls: string;
  section: string;
}

// A student row in the TC list. The backend returns many dynamic fields, so an
// index signature is kept alongside the ones the UI reads directly.
export interface TcStudentRow {
  NewEmisId?: string;
  unique_id_no?: string;
  name?: string;
  name_tamil?: string;
  class_studying?: string;
  class_section?: string;
  gender?: string;
  student_tc_date?: string;
  student_id_in_tc_dtl?: string | null;
  transfer_flag?: string;
  DateFlag?: string;
  mhrd_id?: number;
  // derived on the client
  tc_status?: string;
  formatted_last_date?: string;
  class_and_section?: string;
  [key: string]: unknown;
}

interface ClassListResponse {
  dataStatus: boolean;
  result: TcClassSection[];
  message?: string;
}

interface StudentListResponse {
  result: TcStudentRow[];
  message?: string;
}

// getStuClass() hits Django (tntp) on LOCAL/STAGING and the TNTP PHP repo on
// PRODUCTION; the current/past lists always hit the TNTP PHP repo.
const isProduction = ENV_CONFIG.environment === 'PRODUCTION';

export const studentTcDetailsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // getStuClass(schlId) — SchlWiseClassList
    getTcSchoolClasses: builder.query<
      ClassListResponse,
      { schoolId: number | string }
    >({
      query: ({ schoolId }) =>
        isProduction
          ? {
              url: 'api/SchlWiseClassList',
              method: 'GET',
              params: { SchlId: schoolId },
              service: 'phpTntp',
            }
          : {
              url: 'SchlWiseClassList',
              method: 'GET',
              params: { SchlId: schoolId },
              service: 'djangoTntp',
            },
    }),

    // getCurrectStuList(Cls, Sec) — /api/CrntStuTransferList
    getCurrentStudents: builder.query<
      StudentListResponse,
      { cls: string; sec: string }
    >({
      query: ({ cls, sec }) => ({
        url: 'api/CrntStuTransferList',
        method: 'GET',
        params: { Cls: cls, Sec: sec },
        service: 'phpTntp',
      }),
    }),

    // getPastStuList(Cls, Sec) — /api/PastStuTransferList
    getPastStudents: builder.query<
      StudentListResponse,
      { cls: string; sec: string }
    >({
      query: ({ cls, sec }) => ({
        url: 'api/PastStuTransferList',
        method: 'GET',
        params: { Cls: cls, Sec: sec },
        service: 'phpTntp',
      }),
    }),
  }),
});

// ── Phase 2: edit form ───────────────────────────────────────────────────────
interface DetailResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}
interface RegistrationResponse {
  result: Record<string, unknown>;
  message?: string;
}
interface ClasslistResponse {
  classlist: Record<string, unknown>[];
  message?: string;
}
interface SaveResponse {
  dataStatus: boolean;
  message?: string;
}

export const studentTcEditApi = studentTcDetailsApi.injectEndpoints({
  endpoints: (builder) => ({
    // getcurrentStudentList(schoolId, StuId) — schoolWiseStudentDetails
    getStudentDetail: builder.query<
      DetailResponse,
      { schoolId: number | string; stuId: number | string }
    >({
      query: ({ schoolId, stuId }) => ({
        url: 'api/schoolWiseStudentDetails',
        method: 'GET',
        params: { school_id: schoolId, StuId: stuId },
        service: 'phpTntp',
      }),
    }),

    // studentRegistration(schoolId) — master data for edit form
    getStudentRegistration: builder.query<
      RegistrationResponse,
      { schoolId: number | string }
    >({
      query: ({ schoolId }) => ({
        url: 'api/studentRegistration',
        method: 'GET',
        params: { school_id: schoolId },
        service: 'phpTntp',
      }),
    }),

    // getschoolclasses(schoolId) — Classlist
    getTcClasslist: builder.query<
      ClasslistResponse,
      { schoolId: number | string }
    >({
      query: ({ schoolId }) => ({
        url: 'api/Classlist',
        method: 'GET',
        params: { school_id: schoolId },
        service: 'phpTntp',
      }),
    }),

    // updateStudentRegistrationData(data) — updateStudentDetails
    updateStudentDetails: builder.mutation<
      SaveResponse,
      {
        records: Record<string, unknown>;
        promote_history: Record<string, unknown>;
        constant: string;
      }
    >({
      query: (data) => ({
        url: 'api/updateStudentDetails',
        method: 'POST',
        body: data,
        service: 'phpTntp',
      }),
    }),

    // studentsTransferSave(data) — /api/studentsTransferSave (php3)
    studentsTransferSave: builder.mutation<
      SaveResponse,
      Record<string, unknown>
    >({
      query: (data) => ({
        url: 'api/studentsTransferSave',
        method: 'POST',
        body: { records: data },
        service: 'php3',
      }),
    }),
  }),
});

export const {
  useGetTcSchoolClassesQuery,
  useLazyGetCurrentStudentsQuery,
  useLazyGetPastStudentsQuery,
} = studentTcDetailsApi;

export const {
  useLazyGetStudentDetailQuery,
  useLazyGetStudentRegistrationQuery,
  useLazyGetTcClasslistQuery,
  useUpdateStudentDetailsMutation,
  useStudentsTransferSaveMutation,
} = studentTcEditApi;

// ── Phase 3: transfer flow ───────────────────────────────────────────────────
interface IndividualStudResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}

export const studentTcTransferApi = studentTcDetailsApi.injectEndpoints({
  endpoints: (builder) => ({
    // IndividualStudList({ StudId }) — validate an EMIS id (duplicate check)
    individualStudList: builder.mutation<
      IndividualStudResponse,
      { StudId: string }
    >({
      query: (data) => ({
        url: 'IndividualStudList',
        method: 'POST',
        body: data,
        service: 'djangoEmis',
      }),
    }),

    // updateStudentsTransferDetails(value) — /api/updateStudentsTransferDetails
    updateStudentsTransferDetails: builder.mutation<
      SaveResponse,
      Record<string, unknown>
    >({
      query: (data) => ({
        url: 'api/updateStudentsTransferDetails',
        method: 'POST',
        body: { records: data },
        service: 'php3',
      }),
    }),

    // ooscSurveyData(data) — /api/OoscSurDataCUD
    ooscSurveyData: builder.mutation<
      SaveResponse,
      { records: Record<string, unknown> }
    >({
      query: (data) => ({
        url: 'api/OoscSurDataCUD',
        method: 'POST',
        body: data,
        service: 'php3',
      }),
    }),
  }),
});

export const {
  useIndividualStudListMutation,
  useUpdateStudentsTransferDetailsMutation,
  useOoscSurveyDataMutation,
} = studentTcTransferApi;

// ── Phase 3b: vocational-education exit survey ───────────────────────────────
interface VocationalListResponse {
  result: {
    allstuds: Record<string, unknown>[];
    allpaststuds: Record<string, unknown>[];
  };
  message?: string;
}
interface VocationFormResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}

export const studentTcVocationApi = studentTcDetailsApi.injectEndpoints({
  endpoints: (builder) => ({
    // getVocationalList(cls) — newstudentsTransferList (VE student check)
    getVocationalList: builder.query<VocationalListResponse, { cls: string }>({
      query: ({ cls }) => ({
        url: 'api/newstudentsTransferList',
        method: 'GET',
        params: { Cls: cls },
        service: 'php3',
      }),
    }),

    // getVocationFormData(studId) — VocatiStudcommPoolGet (existing survey data)
    getVocationFormData: builder.query<
      VocationFormResponse,
      { studId: string }
    >({
      query: ({ studId }) => ({
        url: 'api/VocatiStudcommPoolGet',
        method: 'GET',
        params: { StudId: studId },
        service: 'phpTntp',
      }),
    }),

    // postVocationRecords(data) — VocatiStudcommPool (save exit survey)
    postVocationRecords: builder.mutation<
      SaveResponse,
      { records: Record<string, unknown> }
    >({
      query: (data) => ({
        url: 'api/VocatiStudcommPool',
        method: 'POST',
        body: data,
        service: 'phpTntp',
      }),
    }),
  }),
});

export const {
  useLazyGetVocationalListQuery,
  useLazyGetVocationFormDataQuery,
  usePostVocationRecordsMutation,
} = studentTcVocationApi;

// ── Phase 4: bonafide certificate ────────────────────────────────────────────
const isProd = ENV_CONFIG.environment === 'PRODUCTION';

interface MediumStsResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}
interface AppliedStudentResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}
interface PhoneDetailsResponse {
  dataStatus: boolean;
  result: Record<string, unknown>[];
  message?: string;
}
interface BonafideCreateResponse {
  dataStatus: boolean;
  qrLink?: string;
  certificateNo?: string;
  message?: string;
}

export const studentTcBonafideApi = studentTcDetailsApi.injectEndpoints({
  endpoints: (builder) => ({
    // checkSchoolMediumSts(emisId, schoolId)
    checkSchoolMediumSts: builder.query<
      MediumStsResponse,
      { emisId: string; schoolId: number | string }
    >({
      query: ({ emisId, schoolId }) => ({
        url: 'api/checkSchoolMediumSts',
        method: 'GET',
        params: { emisId, schoolId },
        service: 'phpTntp',
      }),
    }),

    // GetAppliedStudentsById(sadId, StuTyp)
    getAppliedStudentById: builder.query<
      AppliedStudentResponse,
      { sadId: string | number; stuTyp: number }
    >({
      query: ({ sadId, stuTyp }) => ({
        url: 'api/GetAppliedStudentById',
        method: 'GET',
        params: { sad_id: sadId, StuTyp: stuTyp },
        service: 'phpTntp',
      }),
    }),

    // getUserPhoneDetails(userId, userType) — HM/teacher details for signature
    getUserPhoneDetails: builder.query<
      PhoneDetailsResponse,
      { userId: string; userType: number }
    >({
      query: ({ userId, userType }) =>
        isProd
          ? {
              url: 'GetPhoneForResetPass',
              method: 'GET',
              params: { userId, userType },
              service: 'djangoEmis',
            }
          : {
              url: 'GetPhoneForResetPass',
              method: 'GET',
              params: { userId, userType },
              service: 'djangoEmis',
            },
    }),

    // bonafideDataCreate(data) — creates the record + returns QR link & cert no
    bonafideDataCreate: builder.mutation<
      BonafideCreateResponse,
      { records: Record<string, unknown> }
    >({
      query: (data) => ({
        url: 'api/bonafideDataCreate',
        method: 'POST',
        body: data,
        service: 'phpTntp',
      }),
    }),
  }),
});

export const {
  useLazyCheckSchoolMediumStsQuery,
  useLazyGetAppliedStudentByIdQuery,
  useLazyGetUserPhoneDetailsQuery,
  useBonafideDataCreateMutation,
} = studentTcBonafideApi;
